// PreSetSetup.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../services/apiClient";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  handleDropPlayer,
  getAvailablePlayers,
  isLiberoAllowed,
  isLineupComplete,
  canStartMatch
} from "./presetSetupUtils";
import { useTheme } from "../config/ThemeContext";  // Import theme from context

const zones = [4, 3, 2, 5, 6, 1];

const getAssignedPlayers = (courtPositions) => {
  return Object.values(courtPositions).map((player) => player?.id);
};

// Draggable Player Component
const PlayerItem = ({ player, onKeySelect, isSelected, isAssigned, team }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "PLAYER",
    item: player,
    canDrag: !isAssigned,
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() })
  }));

  const theme = useTheme();
  const teamColor = theme[team]; // team is "teamA" or "teamB"

  // Check if the player's position is libero (case-insensitive)
  const isLibero = player?.position?.toLowerCase() === "libero";

  // Define styles based on state
  const selectedStyle = `${teamColor.button} text-white shadow-lg`;
  const assignedStyle = "bg-gray-200 text-gray-500";
  const teamDefaultStyle = `${teamColor.bg} hover:${teamColor.hover} border ${teamColor.border} hover:border-opacity-70`;
  const liberoStyle = `${theme.state.libero.bg} hover:${theme.state.libero.hover} border ${theme.state.libero.border}`;

  // Choose which style to apply based on state
  const styleClasses = isSelected 
    ? selectedStyle 
    : isAssigned 
      ? assignedStyle 
      : isLibero 
        ? liberoStyle 
        : teamDefaultStyle;

  return (
    <div
      ref={!isAssigned ? drag : null}
      className={`flex items-center justify-between p-3 mb-2 rounded-lg transition-all ${styleClasses} ${
        isDragging ? "opacity-50" : "opacity-100"
      } shadow-sm cursor-grab active:cursor-grabbing`}
      role="button"
      tabIndex="0"
      aria-label={`Player ${player.shirtNumber} ${player.shirtName} ${isAssigned ? " (Assigned)" : ""}`}
      onKeyDown={(e) => onKeySelect(e, player)}
    >
      <span className="font-medium">
        <span className="font-bold">#{player.shirtNumber}</span> {player.shirtName}
      </span>
      {isAssigned && (
        <span className="text-xs bg-gray-300 px-2 py-1 rounded-full">Assigned</span>
      )}
    </div>
  );
};


// Droppable Court Zone Component
const CourtZone = ({ zone, assignedPlayer, onDropPlayer, team, onKeyDrop, disabled = false }) => {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "PLAYER",
      drop: (item) => onDropPlayer(item, zone, team),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [zone, team]
  );

  const theme = useTheme();
  const teamColor = theme[team];

  // Conditionally disable drop functionality
  const dropRef = disabled ? null : drop;

  // Determine background style
  const baseStyle = disabled
    ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-70"
    : isOver
    ? `${teamColor.hover} ${teamColor.border} scale-[1.02] shadow-md`
    : assignedPlayer
    ? `${teamColor.bg} ${teamColor.border}`
    : "bg-gray-50 border-gray-300";

  return (
    <div
      ref={dropRef}
      className={`w-full min-h-[80px] p-4 rounded-xl transition-all duration-200 flex flex-col items-center justify-center border-2 ${baseStyle}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={
        assignedPlayer
          ? `Zone ${zone}: ${assignedPlayer.firstName} ${assignedPlayer.shirtName}, ${assignedPlayer.position}`
          : `Zone ${zone}, empty`
      }
      onKeyDown={(e) => {
        if (!disabled) onKeyDrop(e, zone, team);
      }}
    >
      <div className={`text-xs font-bold mb-1 ${teamColor.text}`}>Zone {zone}</div>
      {assignedPlayer ? (
        <div className="font-bold text-center">
          <div className="text-lg">#{assignedPlayer.shirtNumber}</div>
          <div className="text-sm text-gray-600">{assignedPlayer.shirtName}</div>
        </div>
      ) : (
        <div className="text-gray-400 italic text-sm">Empty</div>
      )}
    </div>
  );
};


// Main PreSetSetup Component
const PreSetSetup = () => {
  const { matchId, setNumber } = useParams();
  const navigate = useNavigate();
  const [teamAPlayers, setTeamAPlayers] = useState([]);
  const [teamBPlayers, setTeamBPlayers] = useState([]);
  const [courtPositionsA, setCourtPositionsA] = useState({});
  const [courtPositionsB, setCourtPositionsB] = useState({});
  const [teamAName, setTeamAName] = useState("");
  const [teamBName, setTeamBName] = useState("");
  const [teamAId, setTeamAId] = useState(null);
  const [teamBId, setTeamBId] = useState(null);
  const [servingTeam, setServingTeam] = useState(null);
  const [leftTeam, setLeftTeam] = useState(null);
  const [rightTeam, setRightTeam] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [liberosA, setLiberosA] = useState([]);
  const [liberosB, setLiberosB] = useState([]);
  const canStart = canStartMatch(courtPositionsA, courtPositionsB, servingTeam, liberosA, liberosB);
  const [currentSetStatus, setCurrentSetStatus] = useState(null);
const [matchOver, setMatchOver] = useState(false);

  // Get theme from context
  const theme = useTheme();

  const handleKeySelect = (e, player) => {
    if (e.key === "Enter" || e.key === " ") {
      setSelectedPlayer(player);
    }
  };

  const handleKeyDrop = (e, zone, team) => {
    if (e.key === "Enter" && selectedPlayer) {
      const updatedPlayer = {
        ...selectedPlayer,
        currentTeam:
          leftTeam?.id === teamAId
            ? selectedPlayer.team === "teamA"
              ? "teamA"
              : "teamB"
            : selectedPlayer.team === "teamB"
            ? "teamB"
            : "teamA"
      };
      handleDrop(updatedPlayer, zone, team);
      setSelectedPlayer(null);
    }
  };

// In PreSetSetup.js, update the useEffect hook:

useEffect(() => {
  if (!matchId) return;

  const fetchData = async () => {
    try {
const [stateResponse, setsResponse] = await Promise.all([
  apiClient.get(`/api/matches/${matchId}/matchState`),
  Number(setNumber) > 1
    ? apiClient.get(`/api/matches/${matchId}/sets`)
    : Promise.resolve({ data: [] }),
]);

const { teams, playerMap, sideIndicator, matchOver, currentSet, sets } =
  stateResponse.data;

let firstSet = null;
if (Number(setNumber) > 1) {
  firstSet = setsResponse.data.find((s) => s.setNumber === 1);
}
      
      // Set basic match info
      setTeamAId(teams.teamA.id);
      setTeamBId(teams.teamB.id);
      setTeamAName(teams.teamA.name || "Team A");
      setTeamBName(teams.teamB.name || "Team B");
      setMatchOver(matchOver || false);

      // Set current set status (if set exists)
      const currentSetData = sets?.[currentSet];
      setCurrentSetStatus(currentSetData?.status || null);

      // Set serving team (if it's not the first set)
      if (Number(setNumber) > 1 && currentSetData) {
        const lastServerId = currentSetData.servingTeam;
        if (Number(lastServerId) === Number(teams.teamA.id)) {
          setServingTeam(teams.teamB.id);
        } else if (Number(lastServerId) === Number(teams.teamB.id)) {
          setServingTeam(teams.teamA.id);
        }
      }

      // Set side indicator
      if (sideIndicator) {
        setLeftTeam(sideIndicator.left === "teamA" ? teams.teamA : teams.teamB);
        setRightTeam(sideIndicator.right === "teamB" ? teams.teamB : teams.teamA);
      } else {
        setLeftTeam(teams.teamA);
        setRightTeam(teams.teamB);
      }

      // Set players
      setTeamAPlayers(
        Object.entries(playerMap.teamA).map(([id, details]) => ({
          id: Number(id),
          team: "teamA",
          ...details
        }))
      );
      setTeamBPlayers(
        Object.entries(playerMap.teamB).map(([id, details]) => ({
          id: Number(id),
          team: "teamB",
          ...details
        }))
      );

// Choose whether to pre-fill using the first set's starting lineup or the
      // rotation stored in Redis for the current set
      const lineupSource =
        Number(setNumber) > 1 && firstSet
          ? {
              rotationA: firstSet.startingLineupA || [],
              rotationB: firstSet.startingLineupB || [],
              liberoIds: { teamA: firstSet.liberoA || [], teamB: firstSet.liberoB || [] },
            }
          : currentSetData;

      if (lineupSource) {
        const lineupA = lineupSource.rotationA || [];
        const lineupB = lineupSource.rotationB || [];
        
        // Convert lineups to court positions
        const positionsA = {};
        const positionsB = {};
        
        zones.forEach((zone, index) => {
                   if (lineupA[index]) {
            positionsA[zone] = {
              id: lineupA[index],
              ...playerMap.teamA[lineupA[index]],
            };
          }
          if (lineupB[index]) {
            positionsB[zone] = {
              id: lineupB[index],
              ...playerMap.teamB[lineupB[index]],
            };
          }
        });
        
        setCourtPositionsA(positionsA);
        setCourtPositionsB(positionsB);
        
        // Set liberos if they exist
        if (currentSetData.liberoIds) {
          setLiberosA(currentSetData.liberoIds.teamA || []);
          setLiberosB(currentSetData.liberoIds.teamB || []);
        }
      }
    } catch (error) {
      console.error("Error fetching match state:", error);
    }
  };

  fetchData();
}, [matchId, setNumber]);


const isLineupEditable = () => 
  currentSetStatus !== "In Progress" && !matchOver;


  const handleDrop = (player, zone, team) => {
    if (player.team !== team) {
      alert(`You can only assign players to their own court.`);
      return;
    }

    if (!isLiberoAllowed(player, zone)) {
      alert("Liberos can only be assigned to back-row positions (Zones 5, 6, 1).");
      return;
    }

    if (team === "teamA") {
      setCourtPositionsA((prev) => ({ ...prev, [zone]: player }));
    } else {
      setCourtPositionsB((prev) => ({ ...prev, [zone]: player }));
    }
  };

const handleStartSet = async () => {
  // If set exists and is in progress, just navigate

  if (currentSetStatus && currentSetStatus == "In Progress") {
    navigate(`/live-scoring/${matchId}/${setNumber}`);
    return;
  }

  // Otherwise proceed with creating/updating set
  try {
    const response = await apiClient.post("/api/sets/startSet", {
      matchId,
      setNumber,
      startingLineupA: zones.map((zone) => courtPositionsA[zone]?.id || null),
      startingLineupB: zones.map((zone) => courtPositionsB[zone]?.id || null),
      servingTeam,
      liberoA: liberosA,
      liberoB: liberosB
    });

    if (response.data.success) {
      navigate(`/live-scoring/${matchId}/${setNumber}`);
    } else {
      alert(`❌ ${response.data.message || "Failed to start set."}`);
    }
  } catch (error) {
    const msg = error?.response?.data?.message || "Unexpected error starting set.";
    alert(`❌ ${msg}`);
    console.error("Error starting set:", msg);
  }
};

const handleMatchOver = () => {
  navigate('/'); // Navigate to home
};

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${theme.neutral.text} mb-1`}>Set {setNumber} Lineup</h1>
          <div className={`h-1 w-20 ${theme.neutral.border} mx-auto`}></div>
        </div>

        {/* Main Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Players Column */}
          <div className={`lg:w-1/4 bg-white rounded-xl shadow-md p-4 border-l-0 ${theme.teamA.border}`}>
            <h3 className={`text-lg font-semibold mb-4 ${theme.neutral.text} border-b pb-2 flex items-center`}>
              <span className={`w-3 h-3 rounded-full mr-2 ${theme.teamA.dot}`}></span>
              {leftTeam?.id === teamAId ? teamAName : teamBName} Players
            </h3>
<div className="space-y-2">
  {getAvailablePlayers(
    leftTeam?.id === teamAId ? teamAPlayers : teamBPlayers,
    leftTeam?.id === teamAId ? courtPositionsA : courtPositionsB
  )
    .filter((player) => player.position?.toLowerCase() !== "libero")
    .map((player) => (
      <PlayerItem
        key={player.id}
        player={player}
        onKeySelect={handleKeySelect}
        isSelected={selectedPlayer?.id === player.id}
        isAssigned={getAssignedPlayers(
          leftTeam?.id === teamAId ? courtPositionsA : courtPositionsB
        ).includes(player.id)}
        team={leftTeam?.id === teamAId ? "teamA" : "teamB"}
      />
    ))}
</div>

          </div>

          {/* Center Column (Controls & Courts) */}
          <div className="lg:w-2/4">

{/* Control Bar */}
<div className={`bg-white rounded-xl shadow-md p-4 mb-6 flex items-center justify-between`}>
  <div className="flex-1 mr-4">
    <h2 className={`text-lg font-semibold ${theme.neutral.text} mb-2`}>Serving Team</h2>
    <select
      value={servingTeam || ""}
      disabled={currentSetStatus === "In Progress"}
      onChange={(e) => setServingTeam(e.target.value)}
      className="w-full bg-gray-50 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="">Select Serving Team</option>
      <option value={teamAId}>{teamAName}</option>
      <option value={teamBId}>{teamBName}</option>
    </select>
  </div>
  
  <div className="flex flex-col items-end">
    <button
      onClick={matchOver && currentSetStatus === "Completed" ? handleMatchOver : handleStartSet}
      disabled={
        (matchOver && currentSetStatus === "Completed") || 
        (currentSetStatus !== "In Progress" && !canStart)
      }
      className={`px-6 py-3 text-lg font-semibold rounded-lg transition-all ${
        (matchOver && currentSetStatus === "Completed") || 
        (currentSetStatus !== "In Progress" && !canStart)
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : `${theme.teamA.button} text-white shadow-lg hover:shadow-xl transform hover:scale-105`
      }`}
    >
      {matchOver && currentSetStatus === "Completed"
        ? "Match Over - Return Home"
        : currentSetStatus === "In Progress" ? "Continue to Scoring"
        : `Start Set ${setNumber}`}
    </button>
    
    {/* Show validation message when button is disabled due to incomplete setup */}
    {currentSetStatus !== "In Progress" && !canStart && !(matchOver && currentSetStatus === "Completed") && (
      <div className="mt-2 text-sm text-red-600 text-right max-w-xs">
        {!servingTeam && "• Select serving team"}
        {servingTeam && courtPositionsA && !isLineupComplete(courtPositionsA) && (
          <div>• Complete {teamAName} lineup</div>
        )}
        {servingTeam && courtPositionsB && !isLineupComplete(courtPositionsB) && (
          <div>• Complete {teamBName} lineup</div>
        )}
        {servingTeam && courtPositionsA && courtPositionsB && 
         isLineupComplete(courtPositionsA) && isLineupComplete(courtPositionsB) && 
         (liberosA.length === 0 || liberosB.length === 0) && (
          <div>• Select at least 1 libero per team</div>
        )}
      </div>
    )}
  </div>
</div>

            {/* Team A Court */}
            <div className={`bg-white rounded-xl shadow-md p-6 border-t-4 ${theme.teamA.border}`}>
             <div className="flex items-center justify-between mb-4">
			  <h3 className={`text-xl font-semibold flex items-center ${theme.neutral.text}`}>
				<span className={`w-3 h-3 rounded-full mr-2 ${theme.teamA.dot}`}></span>
				{teamAName} Starting Lineup
			  </h3>
			  <button
				onClick={() => setCourtPositionsA({})}
				disabled={!isLineupEditable()}
				className={theme.buttons.tertiary}
			  >
				🗑️ Reset
			  </button>
			</div>
              <div className="grid grid-cols-3 gap-4">
                {zones.map((zone) => (
                  <CourtZone
                    key={zone}
                    zone={zone}
                    assignedPlayer={courtPositionsA[zone]}
                    onDropPlayer={handleDrop}
                    team="teamA"
                    onKeyDrop={handleKeyDrop}
				  disabled={!isLineupEditable()}

                  />
                ))}

              </div>
            </div>
{/* Team A Libero Selection */}
<div className="mt-4">
  <label className="block font-semibold mb-1 text-sm">Select up to 2 Liberos – {teamAName}</label>
  <div className="flex flex-wrap gap-x-4 gap-y-2">
    {teamAPlayers
      .filter(player => player.position?.toLowerCase() === "libero")
      .map(player => (
        <label key={player.id} className="inline-flex items-center space-x-1 text-sm">
          <input
            type="checkbox"
            checked={liberosA.includes(player.id)}
			  disabled={currentSetStatus === "In Progress"}
            onChange={() => {
              if (liberosA.includes(player.id)) {
                setLiberosA(liberosA.filter(id => id !== player.id));
              } else if (liberosA.length < 2) {
                setLiberosA([...liberosA, player.id]);
				}
            }}
          />
          <span>#{player.shirtNumber} – {player.shirtName || player.lastName}</span>
        </label>
      ))}
  </div>
</div>

            {/* Team A Court */}
            <div className={`bg-white rounded-xl shadow-md p-6 border-t-4 ${theme.teamB.border}`}>
             <div className="flex items-center justify-between mb-4">
			  <h3 className={`text-xl font-semibold flex items-center ${theme.neutral.text}`}>
				<span className={`w-3 h-3 rounded-full mr-2 ${theme.teamB.dot}`}></span>
				{teamBName} Starting Lineup
			  </h3>
			  <button
				onClick={() => setCourtPositionsB({})}
				disabled={!isLineupEditable()}
				className={theme.buttons.tertiary}
			  >
				🗑️ Reset
			  </button>
			</div>
              <div className="grid grid-cols-3 gap-4">
                {zones.map((zone) => (
                  <CourtZone
                    key={zone}
                    zone={zone}
                    assignedPlayer={courtPositionsB[zone]}
                    onDropPlayer={handleDrop}
                    team="teamB"
                    onKeyDrop={handleKeyDrop}
				  disabled={!isLineupEditable()}

                  />
                ))}

              </div>
            </div>
			
			{/* Team B Libero Selection */}
<div className="mt-4">
  <label className="block font-semibold mb-1 text-sm">Select up to 2 Liberos – {teamBName}</label>
  <div className="flex flex-wrap gap-x-4 gap-y-2">
    {teamBPlayers
      .filter(player => player.position?.toLowerCase() === "libero")
      .map(player => (
        <label key={player.id} className="inline-flex items-center space-x-1 text-sm">
          <input
            type="checkbox"
			disabled={currentSetStatus === "In Progress"}
            checked={liberosB.includes(player.id)}
            onChange={() => {
              if (liberosB.includes(player.id)) {
                setLiberosB(liberosB.filter(id => id !== player.id));
              } else if (liberosB.length < 2) {
                setLiberosB([...liberosB, player.id]);
              }
            }}
          />
          <span>#{player.shirtNumber} – {player.shirtName || player.lastName}</span>
        </label>
      ))}
  </div>
</div>
			
          </div>
		  
		  

          {/* Right Players Column */}
          <div className={`lg:w-1/4 bg-white rounded-xl shadow-md p-4 border-l-0 ${theme.teamB.border}`}>
            <h3 className={`text-lg font-semibold mb-4 ${theme.neutral.text} border-b pb-2 flex items-center`}>
              <span className={`w-3 h-3 rounded-full mr-2 ${theme.teamB.dot}`}></span>
              {rightTeam?.id === teamBId ? teamBName : teamAName} Players
            </h3>
<div className="space-y-2">
  {getAvailablePlayers(
    rightTeam?.id === teamAId ? teamAPlayers : teamBPlayers,
    rightTeam?.id === teamAId ? courtPositionsA : courtPositionsB
  )
    .filter((player) => player.position?.toLowerCase() !== "libero")
    .map((player) => (
      <PlayerItem
        key={player.id}
        player={player}
        onKeySelect={handleKeySelect}
        isSelected={selectedPlayer?.id === player.id}
        isAssigned={getAssignedPlayers(
          rightTeam?.id === teamAId ? courtPositionsA : courtPositionsB
        ).includes(player.id)}
        team={rightTeam?.id === teamAId ? "teamA" : "teamB"}
      />
    ))}
</div>

          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default PreSetSetup;
